/**
 * Cálculo de disponibilidade — o algoritmo que substitui o olho do barbeiro na
 * agenda de papel.
 *
 * Duas ideias sustentam tudo:
 *
 *  1. **Encadeamento.** O próximo horário livre começa exatamente quando o
 *     anterior termina, e a partir daí anda de `slotStepMinutes` em
 *     `slotStepMinutes`. É isso que faz um serviço de 25 minutos não queimar um
 *     slot de 30 e não deixar buraco morto no dia.
 *
 *     A barbearia pode trocar isso por uma **grade fixa** (`fixedSlotGrid`):
 *     horários contados do começo de cada faixa do expediente, que não se
 *     movem com os atendimentos. A duração continua sendo a do par cliente +
 *     serviço — a grade só decide onde um atendimento pode começar.
 *
 *  2. **Tolerância de sobreposição.** A barbearia decide quantos minutos um
 *     atendimento pode invadir o anterior. Bloqueios (folga, feriado) não têm
 *     tolerância: almoço é almoço.
 *
 * Nada aqui lê relógio, banco ou requisição — `now` entra por parâmetro. É o que
 * permite testar uma sexta-feira lotada de dezembro em dois milissegundos.
 */

import {
  addMinutes,
  type Interval,
  minutesToMs,
  overlapMinutes,
  sortIntervals,
  subtractIntervals,
} from './interval.js';
import type { ScheduleSettings } from './settings.js';
import {
  addCalendarDays,
  type CalendarDate,
  differenceInCalendarDays,
  instantToCalendarDate,
  weekdayOf,
  zonedMinutesToInstant,
} from './time.js';

/**
 * Trava de segurança da varredura. Um dia de 24h com passo de 5 minutos dá 288
 * candidatos; o teto existe para que dado corrompido vire lista vazia em vez de
 * laço infinito segurando a requisição.
 */
const MAX_SCAN_STEPS = 2000;

/** Faixa de trabalho semanal, em minutos locais. */
export interface WorkingHoursRule {
  /** 0 = domingo … 6 = sábado */
  weekday: number;
  startMinute: number;
  endMinute: number;
}

export interface AvailabilityRequest {
  date: CalendarDate;
  timezone: string;
  /** O "agora" da decisão. Entra por parâmetro de propósito. */
  now: Date;
  durationMinutes: number;
  workingHours: readonly WorkingHoursRule[];
  /** Agendamentos que ocupam a agenda do profissional. */
  busy: readonly Interval[];
  /** Folgas, feriados e compromissos. Tolerância zero. */
  blocks: readonly Interval[];
  settings: ScheduleSettings;
}

export interface AvailableSlot {
  start: Date;
  end: Date;
  /** Quantos minutos este horário invade um atendimento vizinho. Quase sempre 0. */
  overlapMinutes: number;
}

export type UnavailableReason =
  /** O profissional não trabalha neste dia da semana. */
  | 'DAY_OFF'
  /** Folga ou feriado cobrindo o expediente inteiro. */
  | 'BLOCKED'
  | 'PAST_DATE'
  /** Além de `maxFutureDays`. */
  | 'BEYOND_HORIZON'
  /** Trabalha, está aberto, mas não sobra espaço para esta duração. */
  | 'FULLY_BOOKED';

export interface AvailabilityResult {
  slots: AvailableSlot[];
  /** Preenchido só quando `slots` vem vazio — a interface explica o porquê. */
  reason?: UnavailableReason;
}

/** Janelas de trabalho do dia, já em instantes UTC. */
export function buildWorkingWindows(
  date: CalendarDate,
  timezone: string,
  rules: readonly WorkingHoursRule[],
): Interval[] {
  const weekday = weekdayOf(date);

  const windows = rules
    .filter((rule) => rule.weekday === weekday && rule.endMinute > rule.startMinute)
    .map((rule) => ({
      start: zonedMinutesToInstant(date, rule.startMinute, timezone),
      end: zonedMinutesToInstant(date, rule.endMinute, timezone),
    }));

  return sortIntervals(windows);
}

/**
 * Os horários da grade fixa num dia da semana, em minutos locais e em ordem.
 *
 * Cada faixa do expediente recomeça a contagem no próprio início: com 09:00 às
 * 11:40 e 13:30 às 20:10, passo de 40, a tarde é 13:30, 14:10… e não a
 * continuação da manhã. É o que deixa o almoço mudar sem a grade da tarde virar
 * hora quebrada. A disponibilidade e a linha do tempo leem daqui — duas contas
 * da mesma grade discordariam no primeiro expediente fora do comum.
 *
 * Com a grade encadeada não existe lista fixa, e a resposta é vazia.
 */
export function fixedGridMinutes(
  rules: readonly WorkingHoursRule[],
  weekday: number,
  settings: Pick<ScheduleSettings, 'fixedSlotGrid' | 'slotStepMinutes'>,
): number[] {
  if (!settings.fixedSlotGrid || settings.slotStepMinutes <= 0) return [];

  const minutes = new Set<number>();
  for (const rule of rules) {
    if (rule.weekday !== weekday) continue;
    for (
      let minute = rule.startMinute;
      minute < rule.endMinute;
      minute += settings.slotStepMinutes
    ) {
      minutes.add(minute);
    }
  }

  return [...minutes].sort((a, b) => a - b);
}

/** Um horário da grade fixa, como a linha do tempo do barbeiro o desenha. */
export interface GridSlotState {
  /** Minuto local em que o horário começa. */
  minute: number;
  /**
   * A partir de quando dá para começar ali. Igual a `minute` quando está
   * limpo; mais tarde quando o atendimento anterior invade a ponta dele; `null`
   * quando o horário já é de alguém.
   */
  freeFrom: number | null;
}

/**
 * Quais horários da grade ainda estão vagos, para o barbeiro tocar e marcar.
 *
 * Um horário é **de alguém** quando um atendimento começa dentro dele, ou
 * quando está coberto do começo ao fim. Quando só a ponta foi invadida — o das
 * 09:00 levou 50 minutos e entra 10 no das 09:40 —, ele continua vago, e
 * `freeFrom` diz a partir de quando. Esconder esse horário apagaria da tela
 * justamente o encaixe que o barbeiro decide na hora; a folha de agendar avisa
 * do conflito quando ele escolher o cliente.
 *
 * Tudo em minutos locais do dia: é desenho, não decisão de agenda. Quem decide o
 * que o cliente pode pedir é `findAvailableSlots`, com a tolerância da casa.
 */
export function gridSlotStates(
  minutes: readonly number[],
  stepMinutes: number,
  occupied: readonly { start: number; end: number }[],
): GridSlotState[] {
  const sorted = [...occupied].sort((a, b) => a.start - b.start);

  return minutes.map((minute) => {
    const slotEnd = minute + stepMinutes;

    if (sorted.some((interval) => interval.start >= minute && interval.start < slotEnd)) {
      return { minute, freeFrom: null };
    }

    // Anda pela cadeia de atendimentos que já estão em curso no começo do
    // horário: um termina e o seguinte, encostado nele, empurra mais um pouco.
    let freeFrom = minute;
    for (const interval of sorted) {
      if (interval.start <= freeFrom && interval.end > freeFrom) freeFrom = interval.end;
    }

    return { minute, freeFrom: freeFrom >= slotEnd ? null : freeFrom };
  });
}

/** Maior invasão que este horário causaria em algum atendimento existente. */
export function worstOverlapMinutes(candidate: Interval, busy: readonly Interval[]): number {
  let worst = 0;
  for (const interval of busy) {
    const minutes = overlapMinutes(candidate, interval);
    if (minutes > worst) worst = minutes;
  }
  return worst;
}

/**
 * Horários que a barbearia pode oferecer ao cliente naquele dia.
 *
 * O barbeiro não passa por aqui quando encaixa alguém na mão: na agenda dele a
 * tolerância é ignorada e o app apenas avisa do conflito (ver `describeConflict`).
 */
export function findAvailableSlots(request: AvailabilityRequest): AvailabilityResult {
  const { date, timezone, now, durationMinutes, settings } = request;

  const today = instantToCalendarDate(now, timezone);
  const daysAhead = differenceInCalendarDays(date, today);
  if (daysAhead < 0) return { slots: [], reason: 'PAST_DATE' };
  if (daysAhead > settings.maxFutureDays) return { slots: [], reason: 'BEYOND_HORIZON' };

  const windows = buildWorkingWindows(date, timezone, request.workingHours);
  if (windows.length === 0) return { slots: [], reason: 'DAY_OFF' };

  const openWindows = subtractIntervals(windows, request.blocks);
  if (openWindows.length === 0) return { slots: [], reason: 'BLOCKED' };

  const busy = sortIntervals(request.busy);
  const earliestStart = addMinutes(now, settings.minLeadTimeMinutes).getTime();
  const stepMs = minutesToMs(settings.slotStepMinutes);
  const tolerance = settings.overlapToleranceMinutes;

  const toleranceMs = minutesToMs(tolerance);
  const durationMs = minutesToMs(durationMinutes);
  const slots: AvailableSlot[] = [];

  if (settings.fixedSlotGrid) {
    // A grade sai da faixa do expediente, e não da janela que sobrou depois de
    // descontar as folgas: um compromisso das 10:00 às 10:30 tira os horários
    // que ele cobre, mas não empurra o resto do dia para 10:30, 11:10…
    for (const minute of fixedGridMinutes(request.workingHours, weekdayOf(date), settings)) {
      const start = zonedMinutesToInstant(date, minute, timezone);
      const end = new Date(start.getTime() + durationMs);

      if (start.getTime() < earliestStart) continue;

      const fits = openWindows.some(
        (window) =>
          window.start.getTime() <= start.getTime() && end.getTime() <= window.end.getTime(),
      );
      if (!fits) continue;

      const overlap = worstOverlapMinutes({ start, end }, busy);
      if (overlap > tolerance) continue;

      slots.push({ start, end, overlapMinutes: overlap });
    }

    return slots.length > 0 ? { slots } : { slots: [], reason: 'FULLY_BOOKED' };
  }

  for (const window of openWindows) {
    const limit = window.end.getTime();
    let cursor = window.start.getTime();

    // A varredura é uma escada só, que se reancora no fim de cada atendimento
    // encontrado. É isso que faz o dia render: depois de um corte de 25 minutos
    // o próximo horário é imediatamente às 10:05, não às 10:15 da grade.
    for (let guard = 0; cursor + durationMs <= limit && guard < MAX_SCAN_STEPS; guard += 1) {
      const start = new Date(cursor);
      const end = new Date(cursor + durationMs);

      const blocking = busy.filter(
        (interval) => overlapMinutes({ start, end }, interval) > tolerance,
      );

      if (blocking.length > 0) {
        const latestEnd = Math.max(...blocking.map((interval) => interval.end.getTime()));
        // O primeiro início aceitável é o fim do atendimento menos a tolerância
        // — é exatamente o encaixe "entra alguns minutos antes de eu terminar".
        const resumeAt = latestEnd - toleranceMs;
        cursor = resumeAt > cursor ? resumeAt : latestEnd;
        continue;
      }

      // Horários cedo demais somem da lista, mas não deslocam a escada: o
      // desenho do dia não pode depender de que horas alguém abriu o app.
      if (cursor >= earliestStart) {
        slots.push({ start, end, overlapMinutes: worstOverlapMinutes({ start, end }, busy) });
      }

      cursor += stepMs;
    }
  }

  slots.sort((a, b) => a.start.getTime() - b.start.getTime());

  return slots.length > 0 ? { slots } : { slots: [], reason: 'FULLY_BOOKED' };
}

export interface DayAvailability {
  date: CalendarDate;
  slots: AvailableSlot[];
  reason?: UnavailableReason;
}

/**
 * Varre vários dias a partir de uma data. Usado pelo "próximo horário livre" e
 * pelo "repetir o último".
 */
export function findAvailabilityAcrossDays(
  request: Omit<AvailabilityRequest, 'date'> & { fromDate: CalendarDate },
  days: number,
): DayAvailability[] {
  const result: DayAvailability[] = [];

  for (let offset = 0; offset < days; offset += 1) {
    const date = addCalendarDays(request.fromDate, offset);
    const { slots, reason } = findAvailableSlots({ ...request, date });
    result.push({ date, slots, reason });
  }

  return result;
}

/** Primeiro horário livre a partir de uma data, ou `null` se não houver. */
export function findNextAvailableSlot(
  request: Omit<AvailabilityRequest, 'date'> & { fromDate: CalendarDate },
  days: number,
): (AvailableSlot & { date: CalendarDate }) | null {
  for (let offset = 0; offset < days; offset += 1) {
    const date = addCalendarDays(request.fromDate, offset);
    const { slots } = findAvailableSlots({ ...request, date });
    const first = slots[0];
    if (first) return { ...first, date };
  }
  return null;
}

/** Um item com tempo próprio — um serviço, na prática. */
export interface FittableItem {
  id: string;
  minutes: number;
}

export interface FittableSubset {
  /** Na mesma ordem em que os itens chegaram. */
  ids: string[];
  durationMinutes: number;
}

/**
 * Acima disto a busca exaustiva deixa de ser exaustiva.
 *
 * Um atendimento real tem dois ou três serviços; o contrato da API já corta
 * bem antes daqui. O teto existe para que dado estranho vire uma resposta
 * gulosa em vez de 2^n varreduras segurando a requisição.
 */
export const MAX_EXHAUSTIVE_ITEMS = 10;

/**
 * O maior conjunto de serviços que ainda cabe no dia.
 *
 * É o que a tela precisa saber para dizer "não cabe cabelo e barba, mas o
 * cabelo sozinho cabe" em vez de "nenhum horário disponível" — a mensagem que
 * manda a pessoa embora sem ela saber que havia uma saída.
 *
 * A pergunta "cabe?" é respondida pela **mesma** `findAvailableSlots` que
 * oferece os horários, e não por uma conta paralela de espaço livre. É o que
 * garante que a redução sugerida aqui realmente produza horários na tela: uma
 * segunda implementação de "o que está livre" acertaria na maioria dos dias e
 * erraria justamente nos apertados, que são estes.
 *
 * Devolve `null` quando nem o menor dos serviços cabe. O conjunto inteiro é um
 * candidato como outro qualquer: quem chama depois de uma lista vazia já sabe
 * que ele não cabia, mas quem chama antes recebe a resposta certa.
 */
export function findLargestFittableSubset(
  request: Omit<AvailabilityRequest, 'durationMinutes'>,
  items: readonly FittableItem[],
): FittableSubset | null {
  if (items.length === 0) return null;

  const fits = fitChecker(request);

  if (items.length > MAX_EXHAUSTIVE_ITEMS) return greedySubset(items, fits);

  const candidates: Array<{ indexes: number[]; minutes: number }> = [];

  for (let mask = 1; mask < 1 << items.length; mask += 1) {
    const indexes: number[] = [];
    let minutes = 0;

    for (let index = 0; index < items.length; index += 1) {
      if (mask & (1 << index)) {
        indexes.push(index);
        minutes += items[index]?.minutes ?? 0;
      }
    }

    candidates.push({ indexes, minutes });
  }

  // Mais serviços primeiro — tirar um da lista é sempre pior do que manter dois.
  // Entre conjuntos do mesmo tamanho, o mais longo: se o dia comporta os 40
  // minutos de cabelo e barba, oferecer os 20 de barba e sobrancelha seria
  // desperdiçar a vaga. O último critério é a ordem em que a pessoa escolheu.
  candidates.sort(
    (a, b) =>
      b.indexes.length - a.indexes.length ||
      b.minutes - a.minutes ||
      (a.indexes[0] ?? 0) - (b.indexes[0] ?? 0),
  );

  for (const candidate of candidates) {
    if (!fits(candidate.minutes)) continue;
    return {
      ids: candidate.indexes.map((index) => items[index]?.id ?? ''),
      durationMinutes: candidate.minutes,
    };
  }

  return null;
}

/**
 * "Cabe um atendimento de tantos minutos neste dia?", com memória.
 *
 * Combinações diferentes somam o mesmo tempo o tempo todo (barba + sobrancelha
 * e cabelo dão 25 minutos os dois), e a resposta depende só do total. Sem o
 * cache, um atendimento de seis serviços varreria o dia 63 vezes.
 */
function fitChecker(
  request: Omit<AvailabilityRequest, 'durationMinutes'>,
): (minutes: number) => boolean {
  const cache = new Map<number, boolean>();

  return (minutes: number) => {
    const known = cache.get(minutes);
    if (known !== undefined) return known;

    const { slots } = findAvailableSlots({ ...request, durationMinutes: minutes });
    const fits = slots.length > 0;
    cache.set(minutes, fits);
    return fits;
  };
}

/** Saída para o caso que não deveria acontecer: tira o mais demorado até caber. */
function greedySubset(
  items: readonly FittableItem[],
  fits: (minutes: number) => boolean,
): FittableSubset | null {
  const remaining = items.map((item, index) => ({ ...item, index }));

  while (remaining.length > 0) {
    const minutes = remaining.reduce((sum, item) => sum + item.minutes, 0);
    if (fits(minutes)) {
      return {
        ids: [...remaining].sort((a, b) => a.index - b.index).map((item) => item.id),
        durationMinutes: minutes,
      };
    }

    let longest = 0;
    for (let index = 1; index < remaining.length; index += 1) {
      if ((remaining[index]?.minutes ?? 0) > (remaining[longest]?.minutes ?? 0)) longest = index;
    }
    remaining.splice(longest, 1);
  }

  return null;
}

export interface ConflictReport {
  /** Nenhuma invasão. */
  clear: boolean;
  overlapMinutes: number;
  /** Cai em cima de folga, feriado ou compromisso. */
  hitsBlock: boolean;
  /** Está fora de qualquer faixa de trabalho do dia. */
  outsideWorkingHours: boolean;
}

/**
 * O que a agenda do barbeiro mostra quando ele arrasta um atendimento para um
 * lugar apertado. Aqui nada é proibido — ele encaixa quem quiser. Só avisamos.
 */
export function describeConflict(input: {
  candidate: Interval;
  timezone: string;
  workingHours: readonly WorkingHoursRule[];
  busy: readonly Interval[];
  blocks: readonly Interval[];
}): ConflictReport {
  const date = instantToCalendarDate(input.candidate.start, input.timezone);
  const windows = buildWorkingWindows(date, input.timezone, input.workingHours);

  const insideSomeWindow = windows.some(
    (window) =>
      input.candidate.start.getTime() >= window.start.getTime() &&
      input.candidate.end.getTime() <= window.end.getTime(),
  );

  const overlap = worstOverlapMinutes(input.candidate, input.busy);
  const hitsBlock = worstOverlapMinutes(input.candidate, input.blocks) > 0;

  return {
    clear: overlap === 0 && !hitsBlock && insideSomeWindow,
    overlapMinutes: overlap,
    hitsBlock,
    outsideWorkingHours: !insideSomeWindow,
  };
}
